import { ASSISTANT_OP_SPECS } from '@/interfaces/assistant.interface';
import type {
  TAssistantKind,
  TAssistantOpName,
} from '@/interfaces/assistant.interface';
import type { IGpthelpToolGate } from './tool-types';
import { switchedOff } from './tool-switch';

/*
 * The proposal kinds and their write tools (plan F10). A leaf module: it
 * imports only the contract, the gate type and the tool switch, so tool
 * modules and the host context can ask which write tool and which ops a
 * member is offered without an import cycle.
 */

/** The write tool of a proposal kind. */
export interface IProposalKind {
  /** The host tool that prepares the kind's ops. */
  name: string;
  /**
   * The member's role may get the tool (plan Y1). Partial permissions keep
   * it: each op their role lacks answers with its lock card.
   */
  roleAllows: (gate: IGpthelpToolGate) => boolean;
}

/** One write tool per proposal kind (plan F2.3). */
export const PROPOSAL_KINDS: Readonly<Record<TAssistantKind, IProposalKind>> = {
  'menu.changes': {
    name: 'menu_proposeChanges',
    // Members who may change the menu or turn products on and off.
    roleAllows: (gate) => gate.canManageCatalog || gate.canDisableCatalog,
  },
  'stock.changes': {
    name: 'stock_proposeChanges',
    // Members with CatalogManage in this branch, what every stock page needs
    // (plan P5-3, Y1): CatalogDisable alone never changes the inventory.
    roleAllows: (gate) => gate.canManageCatalog,
  },
};

const isOpName = (key: string): key is TAssistantOpName =>
  Object.prototype.hasOwnProperty.call(ASSISTANT_OP_SPECS, key);

/** The kind of the op `name`; null for a name this bundle does not know. */
export function kindOfOp(name: string): TAssistantKind | null {
  return isOpName(name) ? ASSISTANT_OP_SPECS[name].kind : null;
}

/**
 * Every op of `kind`, in `ASSISTANT_OP_SPECS` order: the ops its tool reads,
 * whether or not they are offered to the member.
 */
export function opsOfKind(kind: TAssistantKind): TAssistantOpName[] {
  return Object.keys(ASSISTANT_OP_SPECS)
    .filter(isOpName)
    .filter((op) => ASSISTANT_OP_SPECS[op].kind === kind);
}

/**
 * Ops left out below their plan (plan F10, X4): one setPrice per product does
 * setPrices' job on every plan, so a smaller plan never meets a lock it
 * cannot get around. Every other op a plan locks stays offered: the model
 * gets its plan card.
 */
const HIDDEN_BELOW_MIN_TIER: readonly TAssistantOpName[] = ['setPrices'];

/** The store's plan is below what `op` needs, and the op leaves the enum then. */
function hiddenByPlan(
  op: TAssistantOpName,
  gate: Pick<IGpthelpToolGate, 'effectiveTier'>
): boolean {
  return (
    HIDDEN_BELOW_MIN_TIER.includes(op) &&
    gate.effectiveTier < ASSISTANT_OP_SPECS[op].minTier
  );
}

/**
 * The ops of `kind` offered to this member (plan X4): every op but those
 * switched off (killed, or still dark for them) and those left out below
 * their plan. Other ops their role or plan lacks stay: the model gets their
 * lock card.
 */
export function kindOps(
  kind: TAssistantKind,
  gate: Pick<IGpthelpToolGate, 'assistantDisabledOps' | 'effectiveTier'>
): TAssistantOpName[] {
  return opsOfKind(kind).filter(
    (op) => !gate.assistantDisabledOps.includes(op) && !hiddenByPlan(op, gate)
  );
}

/**
 * The write tool of `kind` is available (its `isAvailable`, plan X4, Y1):
 * writes are on for the store, the member's role allows it, and some op is
 * offered, so no definition ever has an empty `op` enum.
 */
export function proposalToolAvailable(
  kind: TAssistantKind,
  gate: IGpthelpToolGate
): boolean {
  return (
    gate.hasRestaurantScope &&
    gate.assistantWritesEnabled &&
    PROPOSAL_KINDS[kind].roleAllows(gate) &&
    kindOps(kind, gate).length > 0
  );
}

/**
 * The write tool of `kind` is offered to this member: available, and not
 * switched off (what the registry's `toolOffered` answers for it).
 */
export function proposalToolOffered(
  kind: TAssistantKind,
  gate: IGpthelpToolGate
): boolean {
  return (
    proposalToolAvailable(kind, gate) &&
    !switchedOff(PROPOSAL_KINDS[kind].name, gate)
  );
}
